import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, TextField,
  CircularProgress, Grid, InputAdornment, IconButton, Pagination,
  FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress,
} from '@mui/material'
import { Search, ExpandMore } from '@mui/icons-material'
import { kbApi } from '../services/api'

const KnowledgeBasePage = () => {
  const [rules, setRules] = useState<any[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [domainFilter, setDomainFilter] = useState('')
  const [domains, setDomains] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [expandedRule, setExpandedRule] = useState<string | null>(null)
  const [tab, setTab] = useState(0)  // 0 = Coverage, 1 = Browse
  const [coverage, setCoverage] = useState<any>(null)
  const pageSize = 20

  useEffect(() => {
    Promise.all([
      kbApi.getRules({ limit: pageSize, offset: 0 }).then(r => setRules(r.data.rules || r.data || [])),
      kbApi.getStats().then(r => setStats(r.data)),
      kbApi.getDomains().then(r => setDomains(r.data.domains || r.data || [])),
      kbApi.getCoverage().then(r => setCoverage(r.data)).catch(() => setCoverage(null)),
    ]).catch(console.error).finally(() => setLoading(false))
  }, [])

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return }
    setSearching(true)
    try {
      const res = await kbApi.semanticSearch({ query: searchQuery, limit: 20 })
      setSearchResults(res.data.results || res.data || [])
    } catch (e) {
      console.error('Search failed:', e)
    } finally {
      setSearching(false)
    }
  }

  const handlePageChange = async (_: any, newPage: number) => {
    setPage(newPage)
    try {
      const params: any = { limit: pageSize, offset: (newPage - 1) * pageSize }
      if (domainFilter) params.domain = domainFilter
      const res = await kbApi.getRules(params)
      setRules(res.data.rules || res.data || [])
    } catch (e) {
      console.error('Failed to fetch rules:', e)
    }
  }

  const handleDomainChange = async (domain: string) => {
    setDomainFilter(domain)
    setPage(1)
    try {
      const params: any = { limit: pageSize, offset: 0 }
      if (domain) params.domain = domain
      const res = await kbApi.getRules(params)
      setRules(res.data.rules || res.data || [])
    } catch (e) {
      console.error('Failed to fetch rules:', e)
    }
  }

  const getSeverityColor = (s: string) => {
    const map: any = { critical: 'error', high: 'warning', medium: 'info', low: 'success' }
    return map[s] || 'default'
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  const displayRules = searchResults || rules

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Knowledge Base</Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.total_rules || stats.total || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Rules</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.domains_count || domains.length}</Typography>
              <Typography variant="body2" color="text.secondary">Domains</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.with_embeddings || 0}</Typography>
              <Typography variant="body2" color="text.secondary">With Embeddings</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.frameworks_count || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Frameworks</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Tab strip — Coverage tree (default) vs Browse rules */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Coverage" />
        <Tab label="Browse Rules" />
      </Tabs>

      {/* Coverage tree — 3-level hierarchy: dimension → category → rules */}
      {tab === 0 && (
        <Box>
          {!coverage && (
            <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Coverage data not available — endpoint may not be deployed yet.
              </Typography>
            </CardContent></Card>
          )}
          {coverage && (
            <>
              {/* Headline */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>What ARIAS covers</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {coverage.total_rules} rules organized into {coverage.total_categories} categories
                    across {coverage.total_dimensions} maturity dimensions, drawn from {coverage.total_frameworks} frameworks
                    plus security (OWASP LLM, OWASP Agentic) and regulatory (EU AI Act, NIST AI RMF, ISO 42001) sources.
                    {coverage.uncategorized_count > 0 && (
                      <Box component="span" sx={{ color: 'warning.main', ml: 1 }}>
                        ({coverage.uncategorized_count} rules pending categorization)
                      </Box>
                    )}
                  </Typography>
                </CardContent>
              </Card>

              {/* One accordion per dimension */}
              {coverage.tree.map((dim: any) => (
                <Accordion key={dim.key} defaultExpanded={dim.rule_count > 100}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        {dim.label}
                      </Typography>
                      <Chip
                        label={`${dim.rule_count} rules`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={`${dim.framework_count} frameworks`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {dim.categories.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No categorized rules yet under this dimension.
                      </Typography>
                    )}
                    {dim.categories.map((cat: any) => {
                      const pct = dim.rule_count > 0 ? (cat.rule_count / dim.rule_count) * 100 : 0
                      return (
                        <Box key={cat.key} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {cat.label}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip label={`${cat.rule_count} rules`} size="small" variant="outlined" />
                              <Chip label={`${cat.framework_count} frameworks`} size="small" variant="outlined" />
                            </Box>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                          />
                          {cat.frameworks?.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {cat.frameworks.slice(0, 8).map((f: string) => (
                                <Chip key={f} label={f} size="small" sx={{ height: 20, fontSize: 10 }} />
                              ))}
                              {cat.frameworks.length > 8 && (
                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                  +{cat.frameworks.length - 8} more
                                </Typography>
                              )}
                            </Box>
                          )}
                          {cat.sample_rules?.length > 0 && (
                            <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'grey.200' }}>
                              {cat.sample_rules.slice(0, 3).map((r: any) => (
                                <Typography key={r.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  • {r.name}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Box>
      )}

      {/* Browse rules — original search + table view (only when tab=1) */}
      {tab === 1 && (<>
      {/* Search + Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                placeholder="Semantic search (e.g., 'prompt injection', 'memory leak')..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} disabled={searching}>
                        {searching ? <CircularProgress size={20} /> : <Search />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Domain</InputLabel>
                <Select
                  value={domainFilter}
                  label="Domain"
                  onChange={e => handleDomainChange(e.target.value)}
                >
                  <MenuItem value="">All Domains</MenuItem>
                  {domains.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              {searchResults && (
                <Chip
                  label={`${searchResults.length} search results`}
                  onDelete={() => setSearchResults(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Dimension</TableCell>
                  {searchResults && <TableCell>Similarity</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRules.map((rule: any) => (
                  <TableRow
                    key={rule.id || rule.rule_id}
                    hover
                    onClick={() => setExpandedRule(expandedRule === (rule.id || rule.rule_id) ? null : (rule.id || rule.rule_id))}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{rule.title || rule.name}</Typography>
                      {expandedRule === (rule.id || rule.rule_id) && rule.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {rule.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={rule.domain} size="small" variant="outlined" /></TableCell>
                    <TableCell>{rule.type}</TableCell>
                    <TableCell>
                      <Chip label={rule.severity || 'medium'} color={getSeverityColor(rule.severity || 'medium') as any} size="small" />
                    </TableCell>
                    <TableCell>{rule.dimension || '-'}</TableCell>
                    {searchResults && (
                      <TableCell>
                        {rule.similarity ? `${(rule.similarity * 100).toFixed(0)}%` : '-'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!searchResults && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil((stats?.total_rules || stats?.total || 100) / pageSize)}
                page={page}
                onChange={handlePageChange}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      </>)}
    </Box>
  )
}

export default KnowledgeBasePage
